import React, { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  AudioDeviceOption,
  useAudioDevices,
  getCurrentOutputDeviceId,
  setCurrentOutputDeviceId,
  getCurrentInputDeviceId,
  setCurrentInputDeviceId,
} from "../utils/audioDevices";
import DecibelMeter from "./DecibelMeter";
import { MicrophoneImpl, WaveformCallback } from "../utils/MicrophoneImpl";
import microphoneIconSrc from "../assets/microphone.svg";
import speakerIconSrc from "../assets/speaker.svg";
import speakerSlashIconSrc from "../assets/speaker-slash.svg";
import tickIconSrc from "../assets/tick.svg";
import "../styles/components/AudioControls.css";

interface AudioControlsProps {
  onAudioSpeakerChange: (deviceId: string) => void;
  onAudioSpeakerMuteChange: (muted: boolean) => void;
  audioSpeakerMuteGetter: () => boolean;
  onAudioSpeakerVolumeChange: (volume: number) => void;
  audioSpeakerVolumeGetter: () => number;
}

// Function to calculate RMS volume from audio data
const calculateRMS = (audioData: Int16Array): number => {
  if (audioData.length === 0) return 0;

  let sum = 0;
  for (let i = 0; i < audioData.length; i++) {
    // Convert from Int16 (-32768 to 32767) to normalized value
    const sample = audioData[i] / 32768.0;
    sum += sample * sample;
  }

  // Calculate RMS (Root Mean Square)
  const rms = Math.sqrt(sum / audioData.length);

  // Apply some scaling for better visualization (adjust as needed)
  // Log scale can work well for audio levels
  const scaledRMS = Math.min(1, Math.max(0, rms > 0 ? 1 + Math.log10(rms) / 2 : 0));

  return scaledRMS;
};

const AudioControls: React.FC<AudioControlsProps> = ({
  onAudioSpeakerChange,
  onAudioSpeakerMuteChange,
  audioSpeakerMuteGetter,
  onAudioSpeakerVolumeChange,
  audioSpeakerVolumeGetter,
}) => {
  const { t } = useTranslation();
  const { audioInputDevices, audioOutputDevices, isLoading, error } = useAudioDevices();
  const [selectedMicId, setSelectedMicId] = useState<string>("");
  const [selectedSpeakerId, setSelectedSpeakerId] = useState<string>("");
  const [volumeLevel, setVolumeLevel] = useState<number>(0);
  const [speakerMuted, setSpeakerMuted] = useState<boolean>(audioSpeakerMuteGetter());
  const [speakerVolume, setSpeakerVolume] = useState<number>(audioSpeakerVolumeGetter());

  // Feature detection for audio output device selection
  const supportsOutputSelection = "setSinkId" in HTMLMediaElement.prototype;

  const waveformCallback = useCallback<WaveformCallback>((data, isFirst, isLast, keyType) => {
    const rmsLevel = calculateRMS(data);
    setVolumeLevel(rmsLevel);
  }, []);

  // Set selected devices when devices are loaded, using currently active devices if possible
  useEffect(() => {
    console.log("[AudioControls] device management effect mounting");
    const initializeDevices = async () => {
      try {
        // For microphones - try to get currently active microphone
        if (audioInputDevices.length > 0) {
          const currentMicId = MicrophoneImpl.getInstance().selectedMicrophoneId;

          for (const device of audioInputDevices) {
            console.log("[AudioControls] Device ID:", device.id, " CURRENT:", currentMicId);
            if (device.isDefault) {
              console.log("[AudioControls] Device ID isDefault==true:", device.id);
            }
          }

          if (currentMicId && audioInputDevices.some((device) => device.id === currentMicId)) {
            // Use currently active microphone if it's in the list
            setSelectedMicId(currentMicId);
          } else {
            // Otherwise fallback to default or first device
            const defaultMic =
              audioInputDevices.find((device) => device.isDefault) || audioInputDevices[0];
            setSelectedMicId(defaultMic.id);
          }
        }

        // For speakers - try to get currently active speaker
        if (audioOutputDevices.length > 0) {
          const currentSpeakerId = getCurrentOutputDeviceId();

          if (
            currentSpeakerId &&
            audioOutputDevices.some((device) => device.id === currentSpeakerId)
          ) {
            // Use currently active speaker if it's in the list
            setSelectedSpeakerId(currentSpeakerId);
          } else {
            // Otherwise fallback to default or first device
            const defaultSpeaker =
              audioOutputDevices.find((device) => device.isDefault) || audioOutputDevices[0];
            setSelectedSpeakerId(defaultSpeaker.id);
          }
        }
      } catch (error) {
        console.error("[AudioControls] Error initializing audio devices:", error);
      }
    };

    if (!isLoading) {
      initializeDevices();
    }
  }, [audioInputDevices, audioOutputDevices, isLoading]);

  // Subscribe to microphone waveform data
  useEffect(() => {
    console.log("[AudioControls] waveform tap effect mounting");
    const mic = MicrophoneImpl.getInstance();
    if (mic) {
      mic.addWaveformTap("audiocontrols", waveformCallback);
    }

    return () => {
      console.log("[AudioControls] waveform tap effect unmounting");
      const mic = MicrophoneImpl.getInstance();
      if (mic) {
        mic.removeWaveformTap("audiocontrols");
      }
    };
  }, [waveformCallback]);

  // Function to handle microphone selection and actually change the device
  const handleMicrophoneSelect = async (deviceId: string) => {
    setSelectedMicId(deviceId);
    console.log(`[AudioControls] Selected microphone: ${deviceId}`);
    
    // Save to localStorage for persistence
    setCurrentInputDeviceId(deviceId);

    try {
      // Restart the microphone with the new device
      const mic = MicrophoneImpl.getInstance();
      if (mic && mic.started) {
        // Stop the current microphone
        mic.stop();

        MicrophoneImpl.getInstance().selectedMicrophoneId = deviceId;

        // Then start it again with the selected device ID
        await mic.start();
        console.log("[AudioControls] Microphone changed successfully");
      }
    } catch (error) {
      console.error("[AudioControls] Error changing microphone device:", error);
    }
  };

  // Function to handle speaker selection
  const handleSpeakerSelect = (deviceId: string) => {
    setSelectedSpeakerId(deviceId);
    console.log(`[AudioControls] Selected speaker: ${deviceId}`);
    setCurrentOutputDeviceId(deviceId);
    onAudioSpeakerChange(deviceId);
  };

  const speakerButtonClicked = () => {
    const newSpeakerMuted = !speakerMuted;
    setSpeakerMuted(newSpeakerMuted);
    onAudioSpeakerMuteChange(newSpeakerMuted);
  };

  return (
    <>
      <div className="audio-settings-section">
        <div className="audio-settings-header">
          <div className="header-title">
            <img src={microphoneIconSrc} alt={t("audioControls.microphoneAlt")} width="20" height="20" />
            {/* <h2 className="no-wrap">Microphone {isLoading ? "loading..." : "settings"}</h2> */}
            <h2 className="no-wrap">{t("audioControls.microphone")} {isLoading ? t("audioControls.loading") : ""}</h2>
          </div>
          <div className="audio-level-meter">
            <DecibelMeter volumeLevel={volumeLevel} />
          </div>
        </div>
        <div className="audio-device-list">
          {isLoading ? (
            <div className="loading-message">{t("audioControls.loadingDevices")}</div>
          ) : error ? (
            <div className="error-message">{error}</div>
          ) : audioInputDevices.length === 0 ? (
            <div className="no-devices-message">{t("audioControls.noMicrophonesFound")}</div>
          ) : (
            audioInputDevices.map((device: AudioDeviceOption) => (
              <div
                key={device.id}
                className={`audio-device-option ${device.id === selectedMicId ? "selected" : ""}`}
                onClick={() => handleMicrophoneSelect(device.id)}
              >
                <span>{device.name}</span>
                {device.id === selectedMicId && (
                  <span className="selected-icon">
                    <img src={tickIconSrc} alt={t("audioControls.selected")} width="20" height="20" />
                  </span>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {supportsOutputSelection && (
        <div className="audio-settings-section">
          <div className="audio-settings-header">
            <div className="header-title">
              <button
                onClick={() => speakerButtonClicked()}
                style={{
                  background: "none",
                  border: "none",
                  padding: 0,
                  cursor: "pointer",
                }}
                aria-label={t("audioControls.speakerSettings")}
              >
                <img
                  src={speakerMuted ? speakerSlashIconSrc : speakerIconSrc}
                  alt={t("audioControls.speakerAlt")}
                  width="20"
                  height="20"
                />
              </button>
              <h2>{t("audioControls.speaker")} {isLoading ? t("audioControls.loading") : ""}</h2>
            </div>
            <div className="volume-slider-container">
              <input
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={speakerVolume}
                onChange={(e) => {
                  const newVolume = parseFloat(e.target.value);
                  setSpeakerVolume(newVolume);
                  onAudioSpeakerVolumeChange(newVolume);
                  e.target.style.setProperty("--val", `${newVolume * 100}%`);
                }}
                className="volume-slider"
                aria-label={t("audioControls.speakerVolume")}
                style={{ "--val": `${speakerVolume * 100}%` } as React.CSSProperties}
              />
            </div>
          </div>
          <div className="audio-device-list">
            {isLoading ? (
              <div className="loading-message">{t("audioControls.loadingDevices")}</div>
            ) : error ? (
              <div className="error-message">{error}</div>
            ) : audioOutputDevices.length === 0 ? (
              <div className="no-devices-message">{t("audioControls.noSpeakersFound")}</div>
            ) : (
              audioOutputDevices.map((device: AudioDeviceOption) => (
                <div
                  key={device.id}
                  className={`audio-device-option ${
                    device.id === selectedSpeakerId ? "selected" : ""
                  }`}
                  onClick={() => handleSpeakerSelect(device.id)}
                >
                  <span>{device.name}</span>
                  {device.id === selectedSpeakerId && (
                    <span className="selected-icon">
                      <img src={tickIconSrc} alt={t("audioControls.selected")} width="20" height="20" />
                    </span>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default AudioControls;
